/* eslint-disable react-hooks/exhaustive-deps */
import dayjs from 'dayjs';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { PropTypes } from 'prop-types';
import React, { useEffect, useMemo, useState, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '@ge/components/button';
import { Icon, Icons } from '@ge/components/icon';
import { Loader, SpinLoader } from '@ge/components/loader';
import { Dialog } from '@ge/components/modal';
import { ScrollingContainer } from '@ge/components/scrolling-container';
import { Tab, Tabs } from '@ge/components/tabs';
import { Tooltip } from '@ge/components/tooltip';
import { useGlobalFilters } from '@ge/feat-analyze/hooks';
import { useDamagesCounts } from '@ge/feat-inspections/data-hooks/use-damage-counts';
import { useDeleteAnnotation } from '@ge/feat-inspections/data-hooks/use-inspections';
import { fetchDamagesResultCount } from '@ge/feat-inspections/services/damages';
import {
  fetchDamagesResultTable,
  getDamageDetailsById,
} from '@ge/feat-inspections/services/damages-result';
import {
  DayJs,
  InspectionTypes,
  Capability,
  DamageTempModes,
  PermissionScope,
  AuthStrategy,
  DamagesTracker,
} from '@ge/models/constants';
import { AuthRender } from '@ge/shared/components/auth-render';
import { EntityDetailsContext } from '@ge/shared/context/entity-details-context';
import { elevations, globalColors, typography } from '@ge/tokens';
import { isEmpty } from '@ge/util/object-utils';
import { useDetachDamage } from '@ge/web-client/src/app/components/entity-details/case-details/data-hooks/use-detach-case-damages';

import {
  DetailContainer,
  SectionContainer,
  EntityCollapsiblePanel,
} from '../entity-details-shared';

import DamageDetailsHeader from './damage-details-header';
import DamageDetailPanel from './damage-details-panel';
import DamageEditPanel from './damage-edit-panel';
import PacIntegrationTable from './pac-integration-table';
import UploadDamageImageDialog from './upload-damage-image';

const StyledDetailContainer = styled(DetailContainer)`
  padding: 0 10px;
`;

const BladeDetailContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const StyledEntityCollapsiblePanel = styled(EntityCollapsiblePanel)`
  background-color: ${globalColors.slate5};
  color: ${globalColors.slate7};
  border-radius: 3px;
  margin-bottom: 18px;
  padding: 12px;
  position: relative;
  > div {
    margin-bottom: 0px;
  }
  .content {
    > section {
      margin-bottom: 30px;
    }
  }
`;

const StyledDamageDetailCollapsiblePanel = styled(EntityCollapsiblePanel)`
  margin-bottom: 18px;
  padding: 12px;
  margin-top: 15px;
  position: relative;
  > div {
    margin-bottom: 0px;
  }
  .content {
    > section {
      margin-bottom: 30px;
    }
  }
  .title {
    overflow: visible;
  }
`;

const CollapsiblePanelDamageDetailHeading = styled.h2`
  font-size: 23px !important;
  letter-spacing: 0.5px;
  line-height: 12px !important;
`;

const CollapsiblePanelHeading = styled.h2`
  font-family: 'Museo Sans';
  font-size: 12px !important;
  letter-spacing: 0.5px;
  line-height: 12px !important;
  font-weight: 700;
  text-transform: uppercase;
`;
const StyledSummary = styled.div`
  padding: 5px 5px 5px 21px;
  font-size: 13px;
  line-height: 18px;
`;
const BladeDetail = styled.div`
  display: flex;
  align-items: center;
`;
const BladeDetailLabel = styled.label`
  color: ${globalColors.grey4};
  margin-right: 4px;
`;
const BladeDetailValue = styled.p`
  margin: 0;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const ImageViewerButton = styled.button`
  border: none;
  background: none;
`;
const ImageViewerLinkIcon = styled(Icon).attrs((props) => ({
  size: 20,
  color: props.theme.layout.textColor,
}))``;
const RawImage = styled.image`
  position: absolute;
  top: 0;
  width: 100%;
`;
const AnnotationImage = styled.image`
  position: absolute;
  cursor: pointer;
  top: 0;
  width: 100%;
`;

const LoaderContainer = styled.div`
  position: relative;
  height: 300px;
  width: 100%;
`;
const ScrollingContainerContent = styled.div``;

const StyledTabs = styled(Tabs)`
  margin-top: 20px;
`;

const TaskIcon = styled(Icon).attrs((props) => ({
  color: props.theme.entityDetails.tasks.details.columnButtonColor,
}))`
  position: absolute;
  left: 0;
`;

const UploadIcon = styled(Icon).attrs((props) => ({
  color: props.theme.entityDetails.tasks.details.columnButtonColor,
}))`
  position: absolute;
  left: 0;
  margin-left: -6px;
  margin-bottom: 6px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: end;
`;

export const ButtonWrapper = styled.div`
  margin: 10px 25px;
  display: flex;
  flex-direction: column;
  &.non_blade {
    position: absolute;
    top: 0;
    right: 0;
  }
  h4,
  button {
    font-weight: ${typography.bold};
    text-transform: uppercase;
  }
  button {
    align-items: center;
    background: none;
    border: 0;
    color: ${(props) => props.theme.entityDetails.tasks.details.columnButtonColor};
    display: flex;
    font-size: 11px;
    margin-bottom: 10px;
    padding: 0 0 0 15px;
    position: relative;
    svg {
    }
    &:last-of-type {
      svg {
        left: 2px;
      }
    }
  }
`;
const StyledDialog = styled(Dialog)`
  margin-top: 10%;
  header {
    border-radius: 4px 4px 0 0;
  }
  footer {
    border-radius: 0 0 4px 4px;
  }
`;

const CancelButton = styled(Button)`
  width: 75px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.caseDamages.cancelBtnBorderColor};
  background: ${(props) => props.theme.caseDamages.detachBtnBgColor};
  font-size: 13px;
  font-weight: 600;
  line-height: 15.6px;
  text-align: center;
  color: ${(props) => props.theme.caseDamages.textColor};
  margin-right: 10px;
`;

const DeleteButton = styled(Button)`
  width: 72px;
  height: 24px
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.caseDamages.confirmBtnBorderColor};
  background: ${(props) => props.theme.caseDamages.confirmBtnBgColor};
  font-size: 13px;
  font-weight: 600;
  line-height: 15.6px;
  text-align: center;
  color: ${(props) => props.theme.caseDamages.textColor};
`;

const DamageDetails = (damageParams) => {
  const { t, ready } = useTranslation(['inspections.damage-details'], {
    useSuspense: false,
  });
  const {
    damagesResultData: { damages },
    hideDetails,
    setDamagesResultData,
    damagesTrackerData,
    setIsDamageUpdated,
    setIsDamageDetailsOpen,
  } = useContext(EntityDetailsContext);
  const history = useHistory();
  const locationRoute = useLocation();
  const filterDate = useStoreState((state) => state.inspections.inspectDateRange);

  const [damagesCounts, setDamagesCounts] = useState({});

  const [damageDetails, setDamageDetails] = useState(null);
  const [inspectionId, setInspectionId] = useState(null);
  const [rawImageUrl, setRawImageUrl] = useState(null);
  const [showUploadImageModal, setshowUploadImageModal] = useState(null);
  const [annotationImageUrl, setAnnotationImageUrl] = useState(null);
  // const [annotationCSS, setAnnotationCSS] = useState({});
  const [summary, setSummary] = useState('');
  const fetchImageDetails = useStoreActions((actions) => actions.inspections.fetchImageDetails);

  const globalFilters = useGlobalFilters();
  const sitesData = useStoreState((state) => state.sites.sites);
  const [bladeSerialNumber, setBladeSerialNUmber] = useState('');
  const [axisNumber, setAxisNumber] = useState('');
  const [imageName, setImageName] = useState('T1234');
  const [location, setLocation] = useState('');
  const [damageID, setDamageID] = useState();
  const [status, setStatus] = useState();
  const [component, setComponent] = useState();
  const [requiredDate, setRequiredDate] = useState();
  const [inspectionDate, setInspectionDate] = useState();
  const [dateIdentified, setDateIdentified] = useState();
  const [length, setLength] = useState();

  const [family, setFamily] = useState();
  const [subFamily, setSubFamily] = useState();
  const [damage, setDamage] = useState();
  const [category, setCategory] = useState();
  const [followUp, setFollowUp] = useState();
  const [action, setAction] = useState();
  const [probableCause, setProbableCause] = useState();
  const [spanZ, setSpanZ] = useState();
  const [chord, setChord] = useState();
  const [width, setWidth] = useState();
  const [isDamageLoading, setIsDamageLoading] = useState(false);
  const [imageComponentHeight, setImageComponenetHeight] = useState(0);
  const [imageId, setImageId] = useState(null);
  const [annotationComment, setAnnotationComment] = useState();
  const [inspectionType, setInspectionType] = useState(null);
  const [damageModes, setDamageModes] = useState(DamageTempModes.VIEW);
  const [enhancementMetada, setEnhancementMetadata] = useState({
    weight: 1,
    gamma: 1,
    brightness: 0,
    contrast: 1,
    invert: 0,
    grayscale: 0,
    blur: 13,
  });
  const [timerId, setTimerId] = useState();
  const [originalImageHeight, setOriginalImageHeight] = useState(null);
  const [originalImageWidth, setOriginalImageWidth] = useState(null);
  const [assetSiteId, setAssetSiteId] = useState(null);
  const [showBladeDetails, setShowBladeDetails] = useState(true);
  const [rul50, setRul50] = useState();
  const [rul90, setRul90] = useState();
  const [rulStartDate, setRulStartDate] = useState();
  const [cat3DispositionDate, setCat3DispositionDate] = useState();
  const [cat3aDispositionDate, setCat3aDispositionDate] = useState();
  const [cat4DispositionDate, setCat4DispositionDate] = useState();
  const [cat5DispositionDate, setCat5DispositionDate] = useState();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { mutate: deleteAnnotation, isLoading: isDeleteAnnotation } = useDeleteAnnotation();
  const { mutateAsync: detachDamages, isLoading: isDetachDamage } = useDetachDamage();
  const { data: damagesResultCount = {} } = useDamagesCounts({
    filterDate: filterDate,
    damagesTrackerData,
  });

  useEffect(() => {
    if (damagesResultCount && Object.keys(damagesResultCount).length > 0) {
      setDamagesCounts(damagesResultCount);
    }
  }, [damagesResultCount]);

  const getDate = (inputDate, state = false) => {
    if (!inputDate) {
      return null;
    }
    const formatedDate = damageParams?.isAssetPanel || state ? (inputDate || 0) * 1000 : inputDate;
    const date = dayjs(formatedDate).format('DD MMM YY');
    return date !== DayJs.INVALID_DATE && date;
  };

  const getImageName = (imageUrl) => {
    const fileName = imageUrl?.split('/').pop();
    const rawName = fileName?.replace('compressed_', '');
    return rawName;
  };

  useEffect(() => {
    if (isEmpty(damageDetails) || (!damageParams?.isAssetPanel && isEmpty(damages))) {
      return;
    }

    let currentDamageData;

    if (damageParams?.isAssetPanel) {
      currentDamageData = damageDetails.annotationResult.data.attributes;
    } else {
      currentDamageData = Object.values(damages).find(
        // 'Damage ID' is an int and 'damageId' is a string
        // making them both strings no matter what for comparison's sake
        (damage) => damage['Damage ID'].toString() === damageParams.damageId.toString(),
      );
    }

    if (currentDamageData === undefined) return;

    setRawImageUrl(damageDetails.signedUrls[0]?.url);
    setAnnotationImageUrl(damageDetails.signedUrls[1]?.url);
    if (damageDetails.reportJsonMetadata?.['REPORT DETAILS']) {
      setSummary(damageDetails.reportJsonMetadata?.['REPORT DETAILS']?.['Summary/Comments']);
    }
    setBladeSerialNUmber(
      currentDamageData && currentDamageData['Blade Serial #']
        ? currentDamageData['Blade Serial #']
        : '',
    );
    if (component && component === 'Blade') {
      setAxisNumber(
        currentDamageData && currentDamageData['Axis #'] <= 0
          ? t('unknown', 'UNKNOWN')
          : currentDamageData['Axis #'],
      );
    }
    setLocation(damageDetails?.annotationResult?.data?.attributes?.location);
    setDamageID(damageDetails?.annotationResult?.data?.id);
    setComponent(damageDetails?.annotationResult?.data?.attributes?.component);
    setRequiredDate(getDate(damageDetails?.annotationResult?.data?.attributes?.followupdate, true));
    setInspectionDate(
      getDate(damageDetails?.annotationResult?.data?.attributes?.inspectiondate, true),
    );
    setInspectionId(damageParams?.isAssetPanel ? '' : currentDamageData['Inspection Id']);
    setAssetSiteId(
      damageParams?.isAssetPanel ? currentDamageData?.assetid : currentDamageData['Asset Site Id'],
    );
    setDateIdentified(
      getDate(damageDetails?.annotationResult?.data?.attributes?.createddate, true),
    );
    setInspectionType(damageParams?.isAssetPanel ? '' : currentDamageData?.InspectionType);
    setImageId(currentDamageData ? damageDetails.annotationResult.data.attributes.object_id : null);
    setImageName(currentDamageData ? getImageName(damageDetails?.signedUrls[0]?.file_name) : '');
    setRulStartDate(getDate(damageDetails?.annotationResult?.data?.attributes?.rulStartDate, true));
    setCat3DispositionDate(
      getDate(damageDetails?.annotationResult?.data?.attributes?.cat3DispositionDate, true),
    );
    setCat3aDispositionDate(
      getDate(damageDetails?.annotationResult?.data?.attributes?.cat3aDispositionDate, true),
    );
    setCat4DispositionDate(
      getDate(damageDetails?.annotationResult?.data?.attributes?.cat4DispositionDate, true),
    );
    setCat5DispositionDate(
      getDate(damageDetails?.annotationResult?.data?.attributes?.cat5DispositionDate, true),
    );
    updateDamageDetails();
  }, [damageDetails, damageParams.damageId, damages]);

  const updateDamageDetails = (data = null) => {
    let details = data ? data : damageDetails;
    setRawImageUrl(details.signedUrls[0]?.url);
    setAnnotationImageUrl(details.signedUrls[1]?.url);
    setStatus(details.annotationResult.data.attributes.status);
    setFamily(details.annotationResult.data.attributes.family);
    setSubFamily(details.annotationResult.data.attributes.sub_family);
    setDamage(details.annotationResult.data.attributes.family_desc);
    setCategory(details.annotationResult.data.attributes.severity);
    setFollowUp(details.annotationResult.data.attributes.source);
    setAction(details.annotationResult.data.attributes.action);
    setProbableCause(details.annotationResult.data.attributes.root_cause);
    setSpanZ(details.annotationResult.data.attributes.z);
    setChord(details.annotationResult.data.attributes.chord);
    setWidth(details.annotationResult.data.attributes.width);
    setLength(details.annotationResult.data.attributes.length);
    setImageId(details.annotationResult.data.attributes.object_id);
    setImageName(getImageName(details?.signedUrls[0]?.file_name));
    setAnnotationComment(details.annotationResult.data.attributes.comments);
    setRul50(getDate(damageDetails?.annotationResult?.data?.attributes?.p50, true));
    setRul90(getDate(damageDetails?.annotationResult?.data?.attributes?.p90, true));
    if (details.annotationResult.data.attributes.image_enhancement_metadata) {
      setEnhancementMetadata(details.annotationResult.data.attributes.image_enhancement_metadata);
    }
  };
  const handleDamageDetailsUpdate = (result) => {
    const currentDamageData = result.annotationResult.data.attributes;
    setRequiredDate(getDate(currentDamageData?.followupdate, true));
    setInspectionDate(getDate(currentDamageData?.inspectiondate, true));
    setDateIdentified(getDate(currentDamageData?.createddate, true));
    setRul50(getDate(currentDamageData?.p50, true));
    setRul90(getDate(currentDamageData?.p90, true));
    setRulStartDate(getDate(currentDamageData?.rulStartDate, true));
    setCat3DispositionDate(getDate(currentDamageData?.cat3DispositionDate, true));
    setCat3aDispositionDate(getDate(currentDamageData?.cat3aDispositionDate, true));
    setCat4DispositionDate(getDate(currentDamageData?.cat4DispositionDate, true));
    setCat5DispositionDate(getDate(currentDamageData?.cat5DispositionDate, true));
    updateDamageDetails(result);
  };

  const onClose = (state = false) => {
    if (state) {
      fetchDamages(true);
      getDamageDetailsById(damageParams['damageId'])
        .then((result) => {
          handleDamageDetailsUpdate(result);
          setDamageDetails(result);
          setDamageModes(DamageTempModes.VIEW);
          setIsDamageLoading(false);
        })
        .catch(() => {
          setIsDamageLoading(false);
        });
    } else {
      setDamageModes(DamageTempModes.VIEW);
      setIsDamageLoading(false);
    }
  };

  const {
    weight = 1,
    gamma = 1,
    brightness = 0,
    contrast = 1,
    invert = 0,
    grayscale = 0,
  } = enhancementMetada;

  const fetchDamages = async (state = false) => {
    let { endDate, startDate } = filterDate;
    let result = await fetchDamagesResultCount(
      { filters: globalFilters, endDate, startDate, status: 'Open' },
      {},
    );
    let damage = 'All';
    let status = 'Open';
    const category = [];
    const damageTracker = damagesTrackerData?.find((damageTracker) => damageTracker?.selected);
    if (damageTracker?.id === DamagesTracker.BLADES_OPEN && damageTracker?.selected) {
      damage = 'Blade';
    } else if (damageTracker?.id === DamagesTracker.ALL_CLOSED && damageTracker?.selected) {
      status = 'Closed';
    } else if (
      damageTracker?.id === DamagesTracker.NON_BLADES_CATEGORY &&
      damageTracker?.selected
    ) {
      damage = 'nonBlade';
      startDate = null;
      endDate = null;
    }
    fetchDamagesResultTable({
      globalFilters,
      sitesData,
      endDate,
      startDate,
      damage,
      category,
      status,
      isDamageDetails: true,
      damageTracker,
      damagesCounts: result,
      isDelete: state,
      setDamagesResultData,
    });
  };

  useEffect(() => {
    if (damageParams && !damageParams?.isAssetPanel) {
      if (isEmpty(damages)) {
        fetchDamages();
      }
      getDamageDetailsById(damageParams['damageId']).then((result) => {
        setDamageDetails(result);
      });
    }
  }, []);

  useEffect(() => {
    if (damageParams && damageParams?.isAssetPanel) {
      getDamageDetailsById(damageParams['damageId']).then((result) => {
        setDamageDetails(result);
      });
    }
  }, []);

  useEffect(() => {
    if (timerId) {
      return () => clearTimeout(timerId);
    }
  }, [timerId]);

  const editDamagesDetails = useMemo(() => {
    return {
      damageID,
      status,
      component,
      requiredDate,
      inspectionDate,
      dateIdentified,
      subFamily,
      category,
      followUp,
      action,
      probableCause,
      spanZ,
      chord,
      width,
      length,
      rul50,
      rul90,
      rulStartDate,
      cat3DispositionDate,
      cat3aDispositionDate,
      cat4DispositionDate,
      cat5DispositionDate,
      location,
      assetSiteId,
      part: family,
      damageType: damage,
    };
  });

  const damageData = useMemo(() => {
    const damageValues1 = [
      {
        access: 'damage_data.family',
        label: 'Part',
        value: family,
      },
      {
        access: 'damage_data.subFamily',
        label: 'Sub Part',
        value: subFamily,
      },
      {
        access: 'damage_data.damage',
        label: 'Damage Type',
        value: damage,
      },
      {
        access: 'damage_data.category',
        label: 'Category',
        value: category,
      },
      {
        access: 'location',
        label: 'Location',
        value: location,
      },
      {
        access: 'damage_data.probableCause',
        label: 'Probable Cause',
        value: probableCause,
      },
      {
        access: 'damage_data.action',
        label: 'Action',
        value: action,
      },
      {
        access: 'damage_data.followUp',
        label: 'Follow-up',
        value: followUp,
      },
      {
        access: 'damage_data.spanZ',
        label: 'Span Z (m)',
        value: spanZ,
      },
    ];
    const damageValues2 = [
      {
        access: 'damage_data.annotationComment',
        label: 'Comment',
        value: annotationComment,
      },
    ];
    return [
      ...damageValues1,
      {
        access: 'damage_data.width',
        label: 'Size SW (mm)',
        value: width,
      },
      {
        access: 'damage_data.length',
        label: 'Size CW (mm)',
        value: length,
      },
      {
        access: 'damage_data.chord',
        label: 'Chord Y(%)',
        value: chord,
      },
      {
        access: 'damage_data.rul_P50',
        label: 'RUL P50',
        value: rul50,
      },
      {
        access: 'damage_data.rul_P90',
        label: 'RUL P90',
        value: rul90,
      },
      ...damageValues2,
    ];
  }, [
    action,
    annotationComment,
    category,
    damage,
    family,
    followUp,
    length,
    probableCause,
    spanZ,
    subFamily,
    width,
    location,
    InspectionTypes,
    rulStartDate,
    cat3aDispositionDate,
    cat4DispositionDate,
    cat5DispositionDate,
    rul50,
    rul90,
    chord,
  ]);

  const clearEventListener = () => {
    window.removeEventListener('resize', handleResize);
  };

  const closeUploadImageModal = useCallback(
    (shouldFetch = false) => {
      setshowUploadImageModal(false);
      onClose(shouldFetch);
    },
    [setshowUploadImageModal],
  );

  const handleResize = () => {
    if (damageParams.isPopOut) {
      setImageComponenetHeight((originalImageHeight / originalImageWidth) * window.innerWidth);
    }
    clearEventListener();
  };

  useEffect(() => {
    if (originalImageHeight && originalImageWidth) {
      handleResize();
      window.addEventListener('resize', handleResize);
    }
  });

  useEffect(() => {
    if (imageId) {
      const getImageDetails = async () => {
        const data = {
          imageId,
        };
        const { attributes } = await fetchImageDetails(data, {});
        setOriginalImageHeight(attributes.height);
        setOriginalImageWidth(attributes.width);
        const { width: w, height: h } = attributes;
        setImageComponenetHeight((h / w) * 780);
      };
      getImageDetails();
    }
  }, [imageId, fetchImageDetails, damage]);

  if (isEmpty(damages) && !damageParams?.isAssetPanel) {
    if (!damageParams.isPopOut) {
      const timeOutId = setTimeout(function () {
        hideDetails();
        setTimerId(timeOutId);
      }, 1000);
    }
    return <Loader />;
  }
  const handleImageClick = () => {
    if (imageId && damageParams.damageId) {
      window.open(`/inspect/image-viewer/${imageId}/${damageParams.damageId}`);
    }
  };
  if (!(ready && damageDetails)) {
    return <Loader />;
  }

  const isBladeComponent = component === 'Blade';

  const damageLabels = [
    {
      label: 'Damage ID',
      access: 'damageId',
    },
    {
      label: 'Status',
      access: 'status',
    },
    { label: 'Component', access: 'component', key: 'component' },
    {
      label: 'Date Identified',
      access: 'dateIdentified',
    },
    ...(isBladeComponent ? [{ label: 'Inspection Date', access: 'inspectionDate' }] : []),
    { label: 'Required Date', access: 'requiredDate' },
    { label: 'RUL Start Date', access: 'rulStartDate' },
    { label: 'Category 3 Disposition Date', access: 'cat3DispositionDate' },
    { label: 'Category 3a Disposition Date', access: 'cat3aDispositionDate' },
    { label: 'Category 4 Disposition Date', access: 'cat4DispositionDate' },
    { label: 'Category 5 Disposition Date', access: 'cat5DispositionDate' },
  ];
  const damageLabelValues = {
    damageId: damageID,
    status,
    component,
    location,
    dateIdentified,
    inspectionDate,
    requiredDate,
    rulStartDate,
    cat3DispositionDate,
    cat3aDispositionDate,
    cat4DispositionDate,
    cat5DispositionDate,
  };
  const handleTabOnClick = (tabName) => {
    if (tabName === 'integrations') {
      setShowBladeDetails(false);
    } else {
      setShowBladeDetails(true);
    }
  };
  const onDamageStatusUpdate = (status) => {
    const currentDamageDetails = JSON.parse(JSON.stringify(damageDetails));
    currentDamageDetails.annotationResult.data.attributes.status = status;
    setDamageDetails(currentDamageDetails);
    setStatus(status);
  };

  const getDamagePannel = (state) => {
    if (damageModes === DamageTempModes.EDIT)
      return (
        <DamageEditPanel
          damage={editDamagesDetails}
          onClose={onClose}
          isDamageLoading={isDamageLoading}
          setIsDamageLoading={setIsDamageLoading}
          setDamageModes={setDamageModes}
          inspectionType={inspectionType}
        />
      );
    else
      return (
        <>
          {state && buttonWrapper(state)}
          <DamageDetailPanel
            damageLabels={damageLabels}
            damageLabelValues={damageLabelValues}
            damageData={damageData}
          />
        </>
      );
  };

  const handleDeleteDamages = async () => {
    detachDamages(
      {
        damageIds: damageID,
      },
      {
        onSuccess: () => {
          deleteAnnotation(
            {
              annotationId: damageID,
            },
            {
              onSuccess: () => {
                fetchDamages(true);
                setIsDamageUpdated(true);
                setIsDamageDetailsOpen(false);
                history.push(locationRoute.pathname);
              },
            },
          );
        },
        onError: (error) => {
          setIsDamageUpdated(false);
          console.log(error);
        },
      },
    );
  };

  const getFooter = () => (
    <>
      <SpinLoader showLoader={isDetachDamage || isDeleteAnnotation} />
      <ButtonContainer onClick={(e) => e.stopPropagation()}>
        <CancelButton onClick={() => setShowDeleteModal(false)}>
          {t('case_damages_dialog.cancel', 'CANCEL')}
        </CancelButton>
        <DeleteButton
          primary
          disabled={isDetachDamage || isDeleteAnnotation}
          onClick={() => handleDeleteDamages()}
        >
          {t('case_damages_dialog.delete', 'DELETE')}
        </DeleteButton>
      </ButtonContainer>
    </>
  );

  const buttonWrapper = (state) => (
    <ButtonWrapper className={state && 'non_blade'}>
      <AuthRender
        capability={Capability.REI_RESULTS}
        description="Edit button"
        siteLevel={false}
        edit
      >
        <Button type="button" onClick={() => setDamageModes(DamageTempModes.EDIT)}>
          <TaskIcon size={11} icon={Icons.PENCIL} />
          {t('damages.edit_damages', 'EDIT DAMAGE')}
        </Button>
      </AuthRender>
      <AuthRender
        capability={Capability.REI_RESULTS}
        description="Edit button"
        siteLevel={false}
        edit
      >
        <Button type="button" onClick={() => setshowUploadImageModal(true)}>
          <UploadIcon rotate={-180} size={13} icon={Icons.EXPORT} />
          {t('damages.upload_image', 'UPLOAD IMAGE')}
        </Button>
      </AuthRender>
      <AuthRender
        capability={Capability.REI_RESULTS}
        delete
        description="Delete button"
        siteLevel={false}
      >
        <Button type="button" onClick={() => setShowDeleteModal(true)}>
          <TaskIcon size={13} icon={Icons.TRASH} />
          {t('damages.delete_damages', 'DELETE DAMAGE')}
        </Button>
      </AuthRender>
    </ButtonWrapper>
  );

  return (
    <StyledDetailContainer>
      <DamageDetailsHeader
        damageDetails={damageDetails}
        onStatusUpdate={onDamageStatusUpdate}
        assetSiteId={assetSiteId}
        status={status}
        isEdited={damageModes === DamageTempModes.EDIT}
      />
      {component && component === 'Blade' && (
        <React.Fragment>
          <StyledEntityCollapsiblePanel
            headerContent={
              <CollapsiblePanelHeading>
                {t('summary_or_comments', 'Report Summary/Comments')}
              </CollapsiblePanelHeading>
            }
          >
            <StyledSummary>{summary}</StyledSummary>
          </StyledEntityCollapsiblePanel>
          {showBladeDetails ? (
            <BladeDetailContainer>
              <BladeDetail>
                <BladeDetailLabel>{t('blade', 'Blade')}</BladeDetailLabel>
                <BladeDetailValue>{bladeSerialNumber}</BladeDetailValue>
              </BladeDetail>
              <BladeDetail>
                <BladeDetailLabel>{t('axis', 'Axis')}</BladeDetailLabel>
                <BladeDetailValue>{axisNumber}</BladeDetailValue>
              </BladeDetail>
              <BladeDetail>
                <BladeDetailLabel>{t('imageName', 'Image Name')}</BladeDetailLabel>
                <Tooltip title={imageName} zIndex={elevations.P20} placement="top">
                  <BladeDetailValue>{imageName}</BladeDetailValue>
                </Tooltip>
              </BladeDetail>
              <BladeDetail>
                <BladeDetailLabel>{t('location', 'Location')}</BladeDetailLabel>
                <BladeDetailValue>{location}</BladeDetailValue>
              </BladeDetail>
              <AuthRender
                capability={Capability.REI_RESULTS}
                edit
                componentssss="results page"
                enforceView
                description="export damages"
                siteLevel={false}
              >
                <ImageViewerButton
                  onClick={() => {
                    window.open(
                      `/inspect/post-process/${inspectionId}?imgId=${imageId}&annoId=${damageParams.damageId}`,
                      '_blank',
                    );
                  }}
                >
                  <ImageViewerLinkIcon icon={Icons.NOTE} />
                </ImageViewerButton>
              </AuthRender>
              <ImageViewerButton onClick={() => handleImageClick()}>
                <ImageViewerLinkIcon icon={Icons.FULL_SCREEN} />
              </ImageViewerButton>
            </BladeDetailContainer>
          ) : null}
        </React.Fragment>
      )}
      <ScrollingContainer autoHide={false}>
        <SectionContainer>
          <StyledTabs small onChangeHandler={(e) => handleTabOnClick(e)}>
            <Tab
              capabilities={[
                { capability: Capability.REI_RESULTS, scopes: [PermissionScope.VIEW] },
              ]}
              label={t('tabs.overview', 'Overview')}
            >
              <ScrollingContainerContent>
                {imageId && imageComponentHeight ? (
                  <>
                    <div style={{ height: imageComponentHeight }}>
                      <svg width="100%" height="100%">
                        {component && component === 'Blade' && (
                          <defs>
                            <filter
                              id={`filterStack_${damageID}`}
                              x="0"
                              y="0"
                              width="100%"
                              height="100%"
                            >
                              <feColorMatrix
                                in="SourceGraphic"
                                result="grayscaleResult"
                                type="matrix"
                                values={`
            ${0.2126 + 0.7874 * (1 - grayscale)} ${0.7152 - 0.7152 * (1 - grayscale)} ${
                                  0.0722 - 0.0722 * (1 - grayscale)
                                } 0 0
            ${0.2126 - 0.2126 * (1 - grayscale)} ${0.7152 + 0.2848 * (1 - grayscale)} ${
                                  0.0722 - 0.0722 * (1 - grayscale)
                                } 0 0
            ${0.2126 - 0.2126 * (1 - grayscale)} ${0.7152 - 0.7152 * (1 - grayscale)} ${
                                  0.0722 + 0.9278 * (1 - grayscale)
                                } 0 0
            0 0 0 1 0`}
                              />
                              <feColorMatrix
                                type="matrix"
                                colorInterpolationFilters="sRGB"
                                in="grayscaleResult"
                                result="invertResult"
                                values={`${1 - 2 * invert} 0 0 0 ${invert}
                      0 ${1 - 2 * invert} 0 0 ${invert}
                      0 0 ${1 - 2 * invert} 0 ${invert}
                      0 0 0 1 0`}
                              />
                              <feComponentTransfer in="invertResult" result="brightnessResult">
                                <feFuncR type="linear" slope={1} intercept={brightness} />
                                <feFuncG type="linear" slope={1} intercept={brightness} />
                                <feFuncB type="linear" slope={1} intercept={brightness} />
                              </feComponentTransfer>
                              <feComponentTransfer in="brightnessResult" result="contrastResult">
                                <feFuncR
                                  type="linear"
                                  slope={contrast}
                                  intercept={-(0.5 * contrast) + 0.5}
                                />
                                <feFuncG
                                  type="linear"
                                  slope={contrast}
                                  intercept={-(0.5 * contrast) + 0.5}
                                />
                                <feFuncB
                                  type="linear"
                                  slope={contrast}
                                  intercept={-(0.5 * contrast) + 0.5}
                                />
                              </feComponentTransfer>
                              <feComponentTransfer in="contrastResult" result="gammaResult">
                                <feFuncR type="gamma" amplitude={1} exponent={gamma} offset="0" />
                                <feFuncG type="gamma" amplitude={1} exponent={gamma} offset="0" />
                                <feFuncB type="gamma" amplitude={1} exponent={gamma} offset="0" />
                              </feComponentTransfer>
                              <feGaussianBlur
                                id="f_gaussianBlur"
                                in="gammaResult"
                                result="blurResult"
                                stdDeviation={weight === 1 || weight === 0 ? 1 : 13}
                              />
                              <feComposite
                                id="f_composite"
                                operator="arithmetic"
                                k1="0"
                                k2={weight}
                                k3={1 - weight}
                                k4="0"
                                in="gammaResult"
                                in2="blurResult"
                              />
                            </filter>
                            <filter
                              id={`annotation_filter_${damageID}`}
                              x="0"
                              y="0"
                              width="100%"
                              height="100%"
                            >
                              <feColorMatrix
                                in="SourceGraphic"
                                result="grayscaleResult"
                                type="matrix"
                                values="
                1 0 0 0 0
                0 1 0 0 0
                0 0 1 0 0
                0 0 0 1 0"
                              ></feColorMatrix>
                              <feComponentTransfer in="grayscaleResult" result="invertResult">
                                <feFuncR type="table" tableValues="0 1"></feFuncR>
                                <feFuncG type="table" tableValues="0 1"></feFuncG>
                                <feFuncB type="table" tableValues="0 1"></feFuncB>
                              </feComponentTransfer>
                              <feComponentTransfer in="invertResult" result="brightnessResult">
                                <feFuncR type="linear" slope="3"></feFuncR>
                                <feFuncG type="linear" slope="3"></feFuncG>
                                <feFuncB type="linear" slope="3"></feFuncB>
                              </feComponentTransfer>
                              <feComponentTransfer in="brightnessResult" result="contrastResult">
                                <feFuncR
                                  type="linear"
                                  slope="3"
                                  intercept="-0.15000000000000002"
                                ></feFuncR>
                                <feFuncG
                                  type="linear"
                                  slope="1.3"
                                  intercept="-0.15000000000000002"
                                ></feFuncG>
                                <feFuncB
                                  type="linear"
                                  slope="1.3"
                                  intercept="-0.15000000000000002"
                                ></feFuncB>
                              </feComponentTransfer>
                              <feComponentTransfer in="contrastResult" result="gammaResult">
                                <feFuncR
                                  type="gamma"
                                  amplitude="1"
                                  exponent="3"
                                  offset="0"
                                ></feFuncR>
                                <feFuncG
                                  type="gamma"
                                  amplitude="1"
                                  exponent="3"
                                  offset="0"
                                ></feFuncG>
                                <feFuncB
                                  type="gamma"
                                  amplitude="1"
                                  exponent="3"
                                  offset="0"
                                ></feFuncB>
                              </feComponentTransfer>
                              <feGaussianBlur
                                id="f_gaussianBlur"
                                in="gammaResult"
                                result="blurResult"
                                stdDeviation="7"
                              ></feGaussianBlur>
                              <feComposite
                                id="f_composite"
                                operator="arithmetic"
                                k1="0"
                                k2="5"
                                k3="-4"
                                k4="0"
                                in="gammaResult"
                                in2="blurResult"
                              ></feComposite>
                            </filter>
                          </defs>
                        )}
                        <RawImage
                          xlinkHref={rawImageUrl}
                          width="100%"
                          alt="original image"
                          filter={`url(#filterStack_${damageID})`}
                        />
                        <AnnotationImage
                          xlinkHref={annotationImageUrl}
                          width="100%"
                          alt="annotation image"
                          filter={`url(#annotation_filter_${damageID})`}
                          onClick={() => handleImageClick()}
                        />
                      </svg>
                    </div>
                  </>
                ) : (
                  component &&
                  imageId && (
                    <LoaderContainer>
                      <Loader />
                    </LoaderContainer>
                  )
                )}
                {component && component === 'Blade' ? (
                  getDamagePannel()
                ) : (
                  <StyledDamageDetailCollapsiblePanel
                    headerContent={
                      <CollapsiblePanelDamageDetailHeading>
                        Damage Detail
                      </CollapsiblePanelDamageDetailHeading>
                    }
                    expanded={true}
                  >
                    {getDamagePannel(true)}
                  </StyledDamageDetailCollapsiblePanel>
                )}
              </ScrollingContainerContent>
            </Tab>
            <Tab
              capabilities={[
                { capability: Capability.REI_RESULTS, scopes: [PermissionScope.VIEW] },
              ]}
              authStrategy={AuthStrategy.ANY}
              label={t('tabs.integrations', 'Integrations')}
            >
              <PacIntegrationTable damageDetails={damageDetails} />
            </Tab>
          </StyledTabs>
        </SectionContainer>
      </ScrollingContainer>
      {showUploadImageModal && (
        <UploadDamageImageDialog
          isOpen={showUploadImageModal}
          onClose={closeUploadImageModal}
          damageDetails={damageDetails}
        />
      )}
      {showDeleteModal && (
        <StyledDialog
          isOpen={true}
          header={t('case_damages_dialog.delete_header_text', 'Delete Damage')}
          onClose={() => setShowDeleteModal(false)}
          footer={getFooter()}
        >
          <span>
            {t('case_damages_dialog.delete_message', 'Do you want to delete the damage?')}
          </span>
        </StyledDialog>
      )}
    </StyledDetailContainer>
  );
};

DamageDetails.propTypes = {
  damageParams: PropTypes.object,
};

export default DamageDetails;
